import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [counts, setCounts] = useState(null);
  const [recent, setRecent] = useState({ quotes: [], haulers: [], affiliates: [], large_jobs: [], consulting: [] });

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    Promise.all([
      api.get("/quotes").catch(() => ({ data: [] })),
      api.get("/haulers").catch(() => ({ data: [] })),
      api.get("/affiliates").catch(() => ({ data: [] })),
      api.get("/large-jobs").catch(() => ({ data: [] })),
      api.get("/consulting").catch(() => ({ data: [] })),
    ]).then(([q, h, a, l, c]) => {
      setRecent({ quotes: q.data, haulers: h.data, affiliates: a.data, large_jobs: l.data, consulting: c.data });
      setCounts({ quotes: q.data.length, haulers: h.data.length, affiliates: a.data.length, large_jobs: l.data.length, consulting: c.data.length });
    });
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20" data-testid="dashboard-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="dashboard">
      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// {user.role.toUpperCase()} CONSOLE</div>
      <h1 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-3 tracking-tighter">
        Welcome, {user.full_name}.
      </h1>
      <p className="text-gray-700 mt-2">{user.email} · {user.company || "—"}</p>

      {user.role === "admin" && counts && (
        <>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-px bg-gray-300 border border-gray-300" data-testid="admin-counts">
            {[
              ["Quotes", counts.quotes],
              ["Haulers", counts.haulers],
              ["Affiliates", counts.affiliates],
              ["Large Jobs", counts.large_jobs],
              ["Consulting", counts.consulting],
            ].map(([l, v]) => (
              <div key={l} className="bg-white p-6">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-gray-500">{l}</div>
                <div className="display-black text-3xl text-[#064E3B] mt-2">{v}</div>
              </div>
            ))}
          </div>

          <section className="mt-14">
            <h2 className="display-black text-2xl uppercase text-[#064E3B]">Latest Quotes</h2>
            <div className="mt-4 border border-gray-300 divide-y divide-gray-200" data-testid="latest-quotes">
              {recent.quotes.slice(0, 8).map(q => (
                <div key={q.id} className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                  <div className="font-semibold">{q.full_name}</div>
                  <div className="text-gray-700">{q.email}</div>
                  <div className="text-gray-700">{q.service_slug}</div>
                  <div className="text-gray-700">{q.city || "—"}, {q.state || "—"}</div>
                  <div className="mono text-xs text-gray-500">{q.created_at.slice(0, 10)}</div>
                </div>
              ))}
              {recent.quotes.length === 0 && <div className="p-4 text-sm text-gray-500">No quotes yet.</div>}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="display-black text-2xl uppercase text-[#064E3B]">Large Job Priority</h2>
            <div className="mt-4 border border-gray-300 divide-y divide-gray-200" data-testid="latest-large-jobs">
              {recent.large_jobs.slice(0, 8).map(j => (
                <div key={j.id} className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm items-center">
                  <div className="font-semibold">{j.organization}</div>
                  <div className="text-gray-700">{j.contact_name}</div>
                  <div className="text-gray-700">{j.unit_count} units</div>
                  <div className="text-gray-700">${j.budget_cad.toLocaleString()}</div>
                  <div className={`mono text-[10px] uppercase tracking-wider ${j.qualified ? "text-[#D97706] font-bold" : "text-gray-500"}`}>
                    {j.qualified ? "★ Qualified" : "Standard"}
                  </div>
                  <div className="mono text-xs text-gray-500">{j.created_at.slice(0, 10)}</div>
                </div>
              ))}
              {recent.large_jobs.length === 0 && <div className="p-4 text-sm text-gray-500">No priority jobs yet.</div>}
            </div>
          </section>
        </>
      )}

      {user.role !== "admin" && (
        <div className="mt-10 border border-gray-300 p-7 bg-gray-50" data-testid="user-console">
          <p className="text-sm text-gray-700">
            You're registered as a <span className="font-bold uppercase">{user.role}</span>. Your dispatch console will populate as jobs route to your account.
          </p>
        </div>
      )}
    </div>
  );
}
